import { executeManifestQuery } from "./modules.service";

// ponytail: mesmo contrato de app-mobile/src/api/ruptura.service.ts — as queries
// do módulo gestao-ruptura (app-dono-modulos/src/gestao_ruptura) devolvem tudo
// como texto, então a conversão para número fica aqui.
const MODULE_ID = "gestao-ruptura";

export type RupturaDimensao = "loja" | "categoria" | "comprador" | "responsavel" | "subproblema" | "fornecedor";

// Na ordem do "Ver por" e dos filtros da lista de produtos.
export const DIMENSOES: RupturaDimensao[] = ["loja", "categoria", "comprador", "responsavel", "subproblema", "fornecedor"];

// Abas da tela. Responsável não tem aba própria: a aba Problema abre primeiro os responsáveis
// (quem age sobre o problema: Comercial, CD, Loja, Supply, como no Vitruvio) e, dentro de cada um, os problemas.
export const ABAS: RupturaDimensao[] = DIMENSOES.filter((d) => d !== "responsavel");

// Agrupador dos cards de primeiro nível de cada aba.
export const agrupadorDaAba = (aba: RupturaDimensao): RupturaDimensao => (aba === "subproblema" ? "responsavel" : aba);

// Opções do "Ver por" são as abas (sem Responsável): "Problema" abre primeiro os responsáveis
// e, dentro de um responsável, os problemas. usadas = agrupadores já abertos no caminho.
export const agrupadorDoVerPor = (dimensao: RupturaDimensao, usadas: RupturaDimensao[]): RupturaDimensao =>
  dimensao === "subproblema" && !usadas.includes("responsavel") ? "responsavel" : dimensao;

export const DIMENSAO_LABELS: Record<RupturaDimensao, string> = {
  loja: "Loja",
  categoria: "Categoria",
  comprador: "Comprador",
  responsavel: "Responsável",
  subproblema: "Problema",
  fornecedor: "Fornecedor",
};

// Dimensões cujo item do "Ver por" mostra também o total de itens dele (qtd_total da linha
// do resumo, dentro do filtro de cima) e quanto desse total está na situação da linha.
export const DIMENSOES_COM_TOTAL: RupturaDimensao[] = ["categoria", "comprador", "fornecedor"];

// Linha "sem valor" de cada dimensão no resumo: Sem categoria / comprador / fornecedor
// (chave "-"), Sem responsável (cod_responsavel 0) e Sem solução aplicável (cod_solucao 0).
// Vai para o fim das opções de filtro.
export const ehSemValor = (dimensao: RupturaDimensao, chave: string) =>
  dimensao === "subproblema" || dimensao === "responsavel" ? chave === "0" : chave === "-";

// Explicação do valor_risco, no tooltip do rótulo "Estimativa de Perda" (mesmo texto do mobile).
export const DICA_ESTIMATIVA_PERDA =
  "Estimativa, a preço de custo, de quanto seria preciso repor para os produtos em ruptura " +
  "voltarem a cobrir alguns dias de venda. Não é faturamento e não desconta o estoque atual.\n\n" +
  "Valor = Média de Venda Diária × Custo unitário × Dias de cobertura (padrão: 7)\n" +
  "Ex.: vende 10 un./dia, custo R$ 5,00 → R$ 350,00";

/** TB_RUPTURA_PROBLEMA.cod_problema — sem linha em TB_RUPTURA_CLASSIF o item é OK (5). */
export const PROBLEMAS: Record<number, string> = {
  1: "Ruptura",
  2: "Ruptura c/ Pedido",
  3: "Excesso",
  4: "Excesso c/ Pedido",
  5: "OK",
  6: "Sem classificar",
};

// Situações que o app expõe (o módulo já filtra o resto). PROBLEMAS segue
// completo porque o histórico do produto ainda pode trazer as demais.
export const SITUACOES = [1, 3, 5];

export interface RupturaGeral {
  data_apuracao: string | null;
  qtd_ruptura: number;
  qtd_excesso: number;
  qtd_ok: number;
  qtd_total: number;
}

export interface RupturaResumo {
  dimensao: RupturaDimensao;
  chave: string;
  rotulo: string;
  qtd_total: number;
  qtd_ruptura: number;
  qtd_excesso: number;
  qtd_ok: number;
  perc_ruptura: number;
  valor_risco: number;
}

export interface RupturaFiltros {
  cod_empresa?: string | null;
  cod_categoria?: string | null;
  cod_comprador?: string | null;
  cod_fornecedor?: string | null;
  cod_problema?: number | null;
  cod_responsavel?: number | null;
  cod_solucao?: number | null;
}

export interface RupturaProduto {
  cod_produto: string;
  descricao: string;
  cod_empresa: string;
  cod_comprador: string;
  nome_comprador: string;
  cod_fornecedor: string;
  fornecedor: string;
  cod_categoria: string;
  categoria: string;
  forma_abastecimento: string | null;
  cod_problema: number;
  problema: string;
  cod_solucao: number;
  // Quem age sobre o problema (0 = sem responsável).
  cod_responsavel: number;
  responsavel: string;
  // Pode trazer o placeholder {empresa_ref} (soluções 17/18): ver resolveEmpresaRef.
  solucao: string;
  solucao_grupo: string;
  acao_sugerida: string;
  cod_empresa_ref: string | null;
  estoque_disponivel: number;
  cobertura: number | null;
  mdv: number;
  qt_min: number;
  qt_max: number;
  qtd_pend_ped_compra: number;
  custo: number;
  dias_entrega_atrasado: number | null;
  valor_risco: number | null;
}

export interface RupturaHistorico {
  dt_inicio: string;
  dt_fim: string | null;
  cod_problema: number;
  problema: string;
  solucao: string | null;
}

export interface RupturaProdutoDetalhe extends RupturaProduto {
  ean: string | null;
  secao: string | null;
  grupo: string | null;
  subgrupo: string | null;
  data_ult_entrada: string | null;
  data_ult_saida: string | null;
  dt_ped_maior: string | null;
  dt_ped_menor: string | null;
  historico: RupturaHistorico[];
}

type Raw = Record<string, unknown>;

const num = (valor: unknown): number => Number(valor ?? 0);
const numOuNull = (valor: unknown): number | null => (valor === null || valor === undefined ? null : Number(valor));
const textoOuNull = (valor: unknown): string | null => (valor === null || valor === undefined ? null : String(valor));

const executar = (query: string, params: object, clientId: string) =>
  executeManifestQuery<Raw[]>(MODULE_ID, query, params, clientId);

// O texto das soluções 17/18 vem como "{empresa_ref} | CRO <= 3 dias": o nome
// da empresa de referência não é gravado, quem resolve é o app (pelo erp_code).
// O histórico (ruptura_produto_historico) traz o texto cru da TB_RUPTURA_SOLUCAO,
// sem forma/produto de referência: o placeholder que sobrar sai do texto.
export const resolveEmpresaRef = (texto: string, nomeEmpresaRef: string | null): string => {
  const comEmpresa = nomeEmpresaRef
    ? texto.replace("{empresa_ref}", nomeEmpresaRef)
    : texto.replace("{empresa_ref} | ", "");
  return comEmpresa
    .replace(/\{[a-z_]+\}\s*/g, "")
    .replace(/\s*[:|]\s*$/, "")
    .trim();
};

// lojas (cod_empresas): as lojas que entram na conta; o seletor "Lojas" da tela as define.
export const getRupturaGeral = async (clientId: string, lojas: string[]): Promise<RupturaGeral | null> => {
  const [r] = await executar("ruptura", { cod_empresas: lojas }, clientId);
  if (!r) return null;
  return {
    data_apuracao: textoOuNull(r.data_apuracao),
    qtd_ruptura: num(r.qtd_ruptura),
    qtd_excesso: num(r.qtd_excesso),
    qtd_ok: num(r.qtd_ok),
    qtd_total: num(r.qtd_total),
  };
};

export const getRupturaResumo = async (
  dimensao: RupturaDimensao,
  filtros: RupturaFiltros,
  clientId: string,
  lojas: string[],
): Promise<RupturaResumo[]> => {
  const rows = await executar(
    "ruptura_resumo",
    {
      agrupador: dimensao,
      cod_empresas: lojas,
      cod_empresa: filtros.cod_empresa ?? null,
      cod_categoria: filtros.cod_categoria ?? null,
      cod_comprador: filtros.cod_comprador ?? null,
      cod_fornecedor: filtros.cod_fornecedor ?? null,
      cod_problema: filtros.cod_problema ?? null,
      cod_responsavel: filtros.cod_responsavel ?? null,
      cod_solucao: filtros.cod_solucao ?? null,
    },
    clientId,
  );
  return rows.map((r) => ({
    dimensao,
    chave: String(r.chave),
    rotulo: String(r.rotulo ?? r.chave),
    qtd_total: num(r.qtd_total),
    qtd_ruptura: num(r.qtd_ruptura),
    qtd_excesso: num(r.qtd_excesso),
    qtd_ok: num(r.qtd_ok),
    perc_ruptura: num(r.perc_ruptura),
    valor_risco: num(r.valor_risco),
  }));
};

const mapProduto = (r: Raw): RupturaProduto => ({
  cod_produto: String(r.cod_produto),
  descricao: textoOuNull(r.descricao) ?? String(r.cod_produto),
  cod_empresa: String(r.cod_empresa),
  cod_comprador: textoOuNull(r.cod_comprador) ?? "-",
  nome_comprador: textoOuNull(r.nome_comprador) ?? "Sem comprador",
  cod_fornecedor: textoOuNull(r.cod_fornecedor) ?? "-",
  fornecedor: textoOuNull(r.fornecedor) ?? "Sem fornecedor",
  cod_categoria: textoOuNull(r.cod_categoria) ?? "-",
  categoria: textoOuNull(r.categoria) ?? "Sem categoria",
  forma_abastecimento: textoOuNull(r.forma_abastecimento),
  cod_problema: num(r.cod_problema),
  problema: textoOuNull(r.problema) ?? PROBLEMAS[num(r.cod_problema)] ?? "",
  cod_solucao: num(r.cod_solucao),
  cod_responsavel: num(r.cod_responsavel),
  responsavel: textoOuNull(r.responsavel) ?? "SEM RESPONSAVEL",
  solucao: textoOuNull(r.solucao) ?? "",
  solucao_grupo: textoOuNull(r.solucao_grupo) ?? "",
  acao_sugerida: textoOuNull(r.acao_sugerida) ?? "",
  cod_empresa_ref: textoOuNull(r.cod_empresa_ref),
  estoque_disponivel: num(r.estoque_disponivel),
  cobertura: numOuNull(r.cobertura),
  mdv: num(r.mdv),
  qt_min: num(r.qt_min),
  qt_max: num(r.qt_max),
  qtd_pend_ped_compra: num(r.qtd_pend_ped_compra),
  custo: num(r.custo),
  dias_entrega_atrasado: numOuNull(r.dias_entrega_atrasado),
  valor_risco: numOuNull(r.valor_risco),
});

export const PRODUTOS_POR_PAGINA = 50;

// Campo de RupturaFiltros que cada dimensão filtra. Única fonte do mapeamento:
// filtro de uma linha do resumo e filtros da lista de produtos saem daqui.
export const CAMPO_DA_DIMENSAO = {
  loja: "cod_empresa",
  categoria: "cod_categoria",
  comprador: "cod_comprador",
  responsavel: "cod_responsavel",
  subproblema: "cod_solucao",
  fornecedor: "cod_fornecedor",
} as const satisfies Record<RupturaDimensao, keyof RupturaFiltros>;

// Filtro de uma dimensão a partir da chave do resumo ("" ou null = sem filtro).
// cod_solucao e cod_responsavel são numéricos; as demais chaves seguem texto.
export const filtroDaDimensao = (dimensao: RupturaDimensao, chave: string | null): RupturaFiltros => ({
  [CAMPO_DA_DIMENSAO[dimensao]]: !chave ? null : dimensao === "subproblema" || dimensao === "responsavel" ? Number(chave) : chave,
});

// Inverso de filtroDaDimensao: o valor do filtro no formato da chave do resumo (null = sem filtro).
export const chaveDoFiltro = (filtros: RupturaFiltros, dimensao: RupturaDimensao): string | null => {
  const valor = filtros[CAMPO_DA_DIMENSAO[dimensao]];
  return valor === null || valor === undefined ? null : String(valor);
};

// Filtro de uma linha do resumo (agrupador+chave) em ruptura_resumo/ruptura_produtos.
export const filtroDaLinha = (item: Pick<RupturaResumo, "dimensao" | "chave">): RupturaFiltros =>
  filtroDaDimensao(item.dimensao, item.chave);

// Lista paginada no banco (ordem estável), só pelos filtros.
export const getRupturaProdutos = async (
  params: { pagina: number; busca?: string; lojas: string[] } & RupturaFiltros,
  clientId: string,
): Promise<RupturaProduto[]> => {
  const rows = await executar(
    "ruptura_produtos",
    {
      // Sem agrupador: a lista sai só pelos filtros, que o usuário pode trocar todos.
      agrupador: null,
      chave: null,
      cod_empresas: params.lojas,
      cod_empresa: params.cod_empresa ?? null,
      cod_categoria: params.cod_categoria ?? null,
      cod_comprador: params.cod_comprador ?? null,
      cod_fornecedor: params.cod_fornecedor ?? null,
      cod_problema: params.cod_problema ?? null,
      cod_responsavel: params.cod_responsavel ?? null,
      cod_solucao: params.cod_solucao ?? null,
      // Trecho do código ou da descrição, ou o código de barras exato.
      busca: params.busca?.trim() || null,
      limite: PRODUTOS_POR_PAGINA,
      deslocamento: params.pagina * PRODUTOS_POR_PAGINA,
    },
    clientId,
  );
  return rows.map(mapProduto);
};

// Totalizadores da lista: mesmos filtros e busca de getRupturaProdutos, menos a situação
// (os totais mostram sempre as três). Itens = produto x loja; qtd_produtos conta o produto uma vez.
export interface RupturaProdutosTotal {
  qtd_total: number;
  qtd_ruptura: number;
  qtd_excesso: number;
  qtd_ok: number;
  qtd_produtos: number;
}

export const getRupturaProdutosTotal = async (
  params: { busca?: string; lojas: string[] } & Omit<RupturaFiltros, "cod_problema">,
  clientId: string,
): Promise<RupturaProdutosTotal> => {
  const [r] = await executar(
    "ruptura_produtos_total",
    {
      cod_empresas: params.lojas,
      cod_empresa: params.cod_empresa ?? null,
      cod_categoria: params.cod_categoria ?? null,
      cod_comprador: params.cod_comprador ?? null,
      cod_fornecedor: params.cod_fornecedor ?? null,
      cod_responsavel: params.cod_responsavel ?? null,
      cod_solucao: params.cod_solucao ?? null,
      busca: params.busca?.trim() || null,
    },
    clientId,
  );
  return {
    qtd_total: num(r?.qtd_total),
    qtd_ruptura: num(r?.qtd_ruptura),
    qtd_excesso: num(r?.qtd_excesso),
    qtd_ok: num(r?.qtd_ok),
    qtd_produtos: num(r?.qtd_produtos),
  };
};

export const getRupturaProdutoDetalhe = async (
  codProduto: string,
  codEmpresa: string,
  clientId: string,
): Promise<RupturaProdutoDetalhe | null> => {
  const params = { cod_produto: codProduto, cod_empresa: codEmpresa };
  const [[row], historico] = await Promise.all([
    executar("ruptura_produto_detalhe", params, clientId),
    executar("ruptura_produto_historico", params, clientId),
  ]);
  if (!row) return null;

  return {
    ...mapProduto(row),
    ean: textoOuNull(row.ean),
    secao: textoOuNull(row.secao),
    grupo: textoOuNull(row.grupo),
    subgrupo: textoOuNull(row.subgrupo),
    data_ult_entrada: textoOuNull(row.data_ult_entrada),
    data_ult_saida: textoOuNull(row.data_ult_saida),
    dt_ped_maior: textoOuNull(row.dt_ped_maior),
    dt_ped_menor: textoOuNull(row.dt_ped_menor),
    historico: historico.map((h) => ({
      dt_inicio: String(h.dt_inicio),
      dt_fim: textoOuNull(h.dt_fim),
      cod_problema: num(h.cod_problema),
      problema: textoOuNull(h.problema) ?? PROBLEMAS[num(h.cod_problema)] ?? "",
      solucao: textoOuNull(h.solucao),
    })),
  };
};
